/* eslint-disable react/no-children-prop */
import React from "react";
import { getRouteApi } from "@tanstack/react-router";
import { useTranslation, Trans } from "react-i18next";
// UI
import { useAppForm } from "@components/form/app-form";
import { Alert, Anchor, Center, Flex, Stack, TextInput } from "@mantine/core";
import { useColors } from "@utils/useColors";
import { SpringOnboardingText } from "@components/Typography";

import {
  SessionAuthLoginRequestBody,
  SessionAuthLoginResponse,
} from "@neuromotion-inc/mightier-ts-sdk";
//
import axios from "axios";
import { useMutation } from "@tanstack/react-query";

const Route = getRouteApi("/spring-health/register");

export function SpringHealthLoginPage(): React.ReactElement {
  const searchParams = Route.useSearch();
  const navigate = Route.useNavigate();
  const { t } = useTranslation();

  const themeColors = useColors();

  const { mutateAsync: sessionAuthLogin } = useMutation({
    mutationFn: async (
      reqBody: SessionAuthLoginRequestBody,
    ): Promise<SessionAuthLoginResponse> => {
      // TODO: fix backendApi so it doesn't automatically redirect upon session
      // auth 401, then we'll be able to move this call into backendApi.
      // Sorry, no time to do this during MW-6266 :/
      return await axios.post("/api/v2/auth/session/login/", reqBody);
    },
    onSuccess: () => {
      navigate({ to: "/spring-health/register/enroll", search: searchParams });
    },
  });

  const form = useAppForm({
    defaultValues: {
      password: "",
    },
    onSubmit: async ({ value, formApi }) => {
      try {
        await sessionAuthLogin({
          email: searchParams.email_address,
          password: value.password,
        });
      } catch (err) {
        formApi.fieldInfo.password.instance?.setErrorMap({
          onSubmit: "Login failed! Please make sure your password is correct.",
        });
      }
    },
  });

  console.log(form.state.errors);

  return (
    <Center px="lg" mt={{ xs: 0, md: "xl" }}>
      <Stack w={{ xs: "100%", md: "60%" }} mt="md">
        <Alert color="blue" radius={16} p="xl">
          <Stack>
            <SpringOnboardingText>
              {t("springHealth.register.login.passwordInfo1")}
            </SpringOnboardingText>
          </Stack>
        </Alert>

        <SpringOnboardingText>
          <Trans
            i18nKey="springHealth.register.login.passwordResetCopy"
            components={{
              Link: (
                <Anchor
                  href="https://account.mightier.com/passwordreset"
                  target="_blank"
                  rel="noopener noreferrer"
                  td="underline"
                />
              ),
            }}
          />
        </SpringOnboardingText>

        <TextInput
          value={searchParams.email_address}
          label={t("loginPage.1_email")}
          labelProps={{ c: themeColors.royal100 }}
          disabled
        />
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <form.AppField
            name="password"
            children={(field) => {
              return (
                <field.Password
                  placeholder={t("loginPage.2_password")}
                  errors={field.state.meta.errors}
                  data-testid="password"
                  styles={{ input: { color: themeColors.royal100 } }}
                />
              );
            }}
          />

          <Flex direction="column" align="flex-end" mt="xl" gap="sm">
            <form.Subscribe
              selector={(state) => [
                state.canSubmit,
                state.isSubmitting,
                state.isDefaultValue,
              ]}
              children={([canSubmit, isSubmitting, isDefaultValue]) => (
                <form.SubmitButton
                  loading={isSubmitting}
                  disabled={!canSubmit || isDefaultValue}
                  data-testid="submit-btn"
                >
                  {t("loginPage.login")}
                </form.SubmitButton>
              )}
            />
          </Flex>
        </form>
      </Stack>
    </Center>
  );
}
